import { navItemVisible } from '../functions/featureFlags.js'

// MainNavbar — icon-only navbar with the org/workspace switchers folded in.
//
// Same chrome as AppNavbar (IconNavbar / `navbar` theme), but uses explicit
// named children instead of childrenAs:'state' + children:APPS so additional
// named-key siblings (AppWorkspaceSwitcher, WorkspaceSwitcher, Vr, future
// breadcrumb pieces) can render alongside the icon row. childrenAs:'state'
// silently overrides named children in DOMQL — explicit keys avoid that.

const appBtn = (state) => ({ extends: 'AppNavbarButton', state })

// Linear layout:
//   Logo · [Org ▾] · Vr · Studio · Marketplace · People · Tickets · Documents · Logs · Vr · Calendar · Chat · Meet · Company(i) · AddApp(+) · More(▼)
//
// Two Vr (brand VerticalLine) separators bracket the first app group:
//   1. between the org switcher and the workspace-content apps,
//   2. between the workspace-content apps and the chrome apps.
// Marketplace returns to the workspace-content group as the 2nd entry,
// glyph swapped from `plus` to `blocks` (stacked lego pieces) so it reads
// as the "installed apps" surface rather than a creator. The bare `+`
// becomes a dedicated AddApp entry routing to /add-apps (creator surface,
// prompt-driven page builder). System Status was removed from the navbar
// — it lives only in the More dropdown now (with the rest of the apps,
// Hr-separated). More (▾) opens DROPDOWN_APPS via the @symbo.ls/dropdown
// plugin (`AppNavbarMoreMenu` body, hover/click handlers on
// AppNavbarButton — see AppNavbar.js).
export const MainNavbar = {
  extends: 'IconNavbar',
  // Drop the IconNavbar chrome so MainNavbar reads as part of one
  // continuous header row rather than a chip-shaped island. AppHeader
  // already supplies the outer flex container. Width: 100% lets the
  // contents span the full row so SecondApps can pin to the right via
  // `marginLeft: auto`.
  background: 'transparent',
  backdropFilter: 'none',
  padding: '0',
  round: '0',
  width: '100%',
  flex: '1 1 0',
  minWidth: '0',
  align: 'center',
  gap: 'Z+V',

  // Read the path from reactive root state, NOT `window.location` —
  // the router updates `state.root.currentPath` via History API, but
  // `window.location.pathname` reads are not reactive deps for DOMQL.
  // Using window.location meant `hide` evaluated once at first paint
  // (on /login → true → hidden) and the navbar stayed hidden post-
  // login until a full page reload because the route change didn't
  // invalidate this binding. Reading from `s.root.currentPath` tracks
  // the SPA route change reactively; window.location is the fallback
  // for the very first paint before the router seeds currentPath.
  hide: (el, s) => {
    const path =
      (s && s.root && s.root.currentPath) ||
      (typeof window !== 'undefined' ? window.location.pathname : '/')
    return /^\/(login|signin|signup|reset-password|forgot-password|verify-email|signup-confirmation)/.test(
      path
    )
  },

  Logo: appBtn({ key: 'logo', icon: 'logov2', route: '/', label: 'shell.app.workspace' }),

  // Org switcher, sitting immediately after the Logo. The workspace-level
  // switcher lives on the studio canvas itself (see WsSwitcher inside
  // WorkspaceViews in canvas/pages/studios.js) — having both here meant the
  // same control showed up twice on /studio routes. The canvas one stays
  // because it sits adjacent to the workspace title and reads as
  // workspace-scoped chrome, while the navbar is for global app navigation.
  AppOrgSwitcher: {},

  // Vertical separator between switchers and the first app group. Bare-key
  // auto-extends brand's `Vr` (VerticalLine). `_1`/`_2` suffix lets DOMQL
  // mount two instances of the same component without key collision; both
  // still auto-extend `Vr` because the resolver strips everything after `_`.
  // Brand `Vr` ships with `theme:'line'` + 1px width but no height, so the
  // `alignSelf: stretch` keeps it visible in a flex row with no content of
  // its own. Margin override trims the brand's `0 D` default to a tighter Z.
  Vr_1: {
    alignSelf: 'stretch',
    margin: '0 Z'
  },

  FirstApps: {
    extends: 'Flex',
    flow: 'x',
    align: 'center',
    gap: 'Z+V',
    Studio: appBtn({ key: 'studio', icon: 'layers', route: '/studio', label: 'shell.app.studio' }),
    // `blocks` (stacked lego pieces) reads as "installed apps" — clearer
    // than the previous generic `grid`/`plus` glyphs.
    Marketplace: appBtn({
      key: 'marketplace',
      icon: 'blocks',
      route: '/marketplace',
      label: 'shell.app.marketplace'
    }),
    // /team-and-agents replaces /people in the navbar — gives the
    // hierarchy view top billing while keeping the flat People directory
    // available from the More dropdown. `people` glyph reads as team /
    // org-chart equally well.
    TeamAndAgents: appBtn({
      key: 'teamandagents',
      icon: 'people',
      route: '/team-and-agents',
      label: 'shell.app.teamAndAgents'
    }),
    Tickets: appBtn({ key: 'tickets', icon: 'check', route: '/tasks', label: 'shell.app.tasks' }),
    Documents: appBtn({
      key: 'documents',
      icon: 'document',
      route: '/notes',
      label: 'shell.app.notes'
    }),
    // /analytics is the project / site visitor dashboard (the main
    // analytics surface). Workspace usage, world-map demographics and the
    // logs surface live under /workspace-analytics (+ /workspace-analytics/
    // logs); the AnalyticsNavbar tab strip links across them. Old /logs URL
    // still redirects (see pages/logs-redirect.js).
    Analytics: appBtn({
      key: 'analytics',
      icon: 'analytics',
      route: '/analytics',
      label: 'shell.app.analytics'
    })
  },

  // The global search pill. Definition + dropdown wiring now live in the
  // standalone `HeaderSearch` component (shared/components/HeaderSearch.js);
  // the bare key auto-extends it. MainNavbar only sets the middle-slot
  // layout (stretch to fill the row); the flat AppHeader pins it to a fixed
  // centered pill instead.
  HeaderSearch: {
    flex: '1 1 0',
    minWidth: '0',
    maxWidth: 'none',
    margin: '0 A'
  },

  // SecondApps (chrome group) — pinned to the right edge via
  // `marginLeft: auto`. Vr_2 follows AFTER the group so the vertical
  // rule sits on the RIGHT side of group 2 (between the chrome apps
  // and the AppHeader's UserActions cluster).
  SecondApps: {
    extends: 'Flex',
    flow: 'x',
    align: 'center',
    gap: 'Z+V',
    marginLeft: 'auto',
    Calendar: appBtn({
      key: 'calendar',
      icon: 'date outline',
      route: '/calendar',
      label: 'shell.app.calendar'
    }),
    // "Team chat" (not just "Chat") differentiates from the header's
    // Messenger button ("Direct messages") — workspace-bug #2184. Glyph
    // stays `chat` (multi-dot speech-bubble = group chat); Messenger uses
    // `sfMessage` (single bubble = 1:1 DM).
    Chat: appBtn({ key: 'chat', icon: 'chat', route: '/chat', label: 'shell.app.chat' }),
    // Mail (/mail). MainNavbar names its children explicitly instead of
    // filtering a list through navItemVisible the way AppNavbar does, so the
    // feature gate has to ride on the entry itself — an ungated Mail button
    // would render for every workspace while the `mail` flag is still off.
    // `subscribeTo` re-runs this `hide` when the flag flips at /flags (a
    // root-state read inside a factory does not auto-subscribe).
    Mail: {
      ...appBtn({ key: 'mail', icon: 'email', route: '/mail', label: 'shell.app.mail' }),
      subscribeTo: ['_featureFlags', 'workspace'],
      hide: (el, s) => !navItemVisible(s && s.root, '/mail')
    },
    // Meet opens the new-call modal in place (MeetNewRoomModal, shell-mounted)
    // instead of routing to /meet. Seeds newRoomDraft like the /meet page does.
    Meet: {
      ...appBtn({ key: 'meet', icon: 'video', label: 'shell.app.meet' }),
      onClick: (e, el) => {
        if (e && e.preventDefault) e.preventDefault()
        if (e && e.stopPropagation) e.stopPropagation()
        el.call('openNewRoomModal', {
          name: '',
          kind: 'room',
          transcribeEnabled: true,
          isPrivate: false
        })
      }
    },
    // Company removed from the main row — still accessible via the
    // More dropdown (DROPDOWN_APPS in AppNavbar.js). Keeps the chrome
    // group tight; Company is an infrequent destination.
    // `+` opens the AddApps creator page (prompt-driven page builder
    // backed by the freestyler logic + SDK AI service). Marketplace's
    // browse surface is now in FirstApps with the `blocks` glyph.
    AddApp: appBtn({ key: 'addapps', icon: 'plus', route: '/add-app', label: 'shell.app.addApp' }),
    More: appBtn({ key: 'more', icon: 'arrowAngleDown', label: 'shell.app.more' })
  },

  // Vertical separator on the RIGHT side of SecondApps — visually
  // closes the navbar's content row and divides the in-app chrome
  // (left + middle + right groups) from the AppHeader's external
  // UserActions cluster (Messenger / Notifications / Profile / etc.).
  Vr_2: {
    alignSelf: 'stretch',
    margin: '0 Z'
  }
}
